import { useEffect, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import { setTab, saveScrollPosition } from 'services/salesHistorySlice/salesHistorySlice';
import { useDeviceInfo } from 'hooks/useDeviceInfo';
import HeaderV2 from './components/HeaderV2';
import Filter from './components/Filter';
import HistoryTab from './containers/HistoryTab';
import AllTab from './containers/AllTab';

const VALID_TABS = ['all', 'sold', 'waiting_on_title'];

const PageWrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: calc(100dvh - 72px);
  overflow: hidden;
`;

const PageContent = styled.div`
  margin: 0 auto;
  width: 100%;
  max-width: 2400px;
  padding: 0 24px;
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
`;

const TabPanelsWrapper = styled.div`
  flex: 1;
  min-height: 0;
  position: relative;
  margin-top: 24px;
`;

const TabPanel = styled.div<{ $active: boolean }>`
  display: ${({ $active }) => ($active ? 'block' : 'none')};
  overflow-y: auto;
  height: 100%;
`;

const VehicleSalesHistoryV2 = () => {
  const { isMobile } = useDeviceInfo();
  // Prevent document-level scroll while this page is mounted
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const { tab, scrollPositions } = useSelector((state: any) => state.salesHistory);
  const isMounted = useRef(false);
  const tabPanelsRef = useRef<HTMLDivElement>(null);
  const [activated, setActivated] = useState<Record<string, boolean>>({ [tab]: true });

  useEffect(() => {
    if (!activated[tab]) {
      setActivated(prev => ({ ...prev, [tab]: true }));
    }
  }, [tab]);

  // Continuously save scroll position of the active panel (throttled)
  useEffect(() => {
    const wrapper = tabPanelsRef.current;
    if (!wrapper) return;

    const panel = wrapper.querySelector<HTMLElement>(`[data-tab-key="${tab}"]`);
    if (!panel) return;

    let rafId: number | null = null;
    const onScroll = () => {
      if (rafId) return;
      rafId = requestAnimationFrame(() => {
        console.log('saving stuff', tab, panel.scrollTop);
        dispatch(saveScrollPosition({ tab, position: panel.scrollTop }));
        rafId = null;
      });
    };

    panel.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      panel.removeEventListener('scroll', onScroll);
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, [tab, dispatch]);

  // Restore scroll position on mount
  useEffect(() => {
    const wrapper = tabPanelsRef.current;
    if (!wrapper) return;

    console.log(scrollPositions, 'scrollPositions');

    for (const key in scrollPositions) {
      const savedPos = scrollPositions[key];
      if (savedPos) {
        requestAnimationFrame(() => {
          const panel = wrapper.querySelector<HTMLElement>(`[data-tab-key="${key}"]`);
          console.log(panel, 'panel');
          panel?.scrollTo(0, savedPos);
        });
      }
    }
    // const savedPos = scrollPositions[tab];
    // if (savedPos) {
    //   requestAnimationFrame(() => {
    //     const panel = wrapper.querySelector<HTMLElement>(`[data-tab-key="${tab}"]`);
    //     panel?.scrollTo(0, savedPos);
    //   });
    // }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // URL → Redux: on mount read ?tab from URL; on browser back re-sync
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const urlTab = params.get('tab');
    if (urlTab && VALID_TABS.includes(urlTab) && urlTab !== tab) {
      dispatch(setTab(urlTab));
    }
  }, [location.search]);

  // Redux → URL: skip on initial mount (effect above handles that), then keep URL in sync
  useEffect(() => {
    if (!isMounted.current) {
      isMounted.current = true;
      return;
    }

    const params = new URLSearchParams(location.search);
    if (params.get('tab') !== tab) {
      params.set('tab', tab);
      history.replace({ search: params.toString() });
    }
  }, [tab]);

  return (
    <PageWrapper>
      <HeaderV2 />
      <div style={{ height: '80px' }} />
      <PageContent>
        <Filter tabPanelsRef={tabPanelsRef} />
        <TabPanelsWrapper ref={tabPanelsRef}>
          {isMobile ? (
            <TabPanel data-tab-key="all" data-tab-panel="active" $active>
              <AllTab active skip={false} />
            </TabPanel>
          ) : (
            <>
              <TabPanel data-tab-key="all" data-tab-panel={tab === 'all' ? 'active' : ''} $active={tab === 'all'}>
                <AllTab active={tab === 'all'} skip={!activated['all']} />
              </TabPanel>
              <TabPanel data-tab-key="sold" data-tab-panel={tab === 'sold' ? 'active' : ''} $active={tab === 'sold'}>
                <HistoryTab tabKey="sold" active={tab === 'sold'} skip={!activated['sold']} />
              </TabPanel>
              <TabPanel
                data-tab-key="waiting_on_title"
                data-tab-panel={tab === 'waiting_on_title' ? 'active' : ''}
                $active={tab === 'waiting_on_title'}
              >
                <HistoryTab
                  tabKey="waiting_on_title"
                  active={tab === 'waiting_on_title'}
                  skip={!activated['waiting_on_title']}
                />
              </TabPanel>
            </>
          )}
        </TabPanelsWrapper>
      </PageContent>
    </PageWrapper>
  );
};

export default VehicleSalesHistoryV2;
